/**
 * Image processing utilities for client-side compression and resizing.
 */

export interface ResizeOptions {
    maxDimension: number;
    quality: number;
    mimeType: string;
}

const DEFAULT_OPTIONS: ResizeOptions = {
    maxDimension: 2000,
    quality: 0.8,
    mimeType: 'image/jpeg'
};

/**
 * Resizes an image file based on the provided options.
 * Useful for reducing file size and ensuring compatibility before uploading.
 */
export const resizeImage = async (
    file: File,
    options: Partial<ResizeOptions> = {}
): Promise<File> => {
    const opts = {
        ...DEFAULT_OPTIONS,
        ...options,
        // Use source mime type if it's PNG/WEBP to preserve transparency, otherwise JPEG
        mimeType: options.mimeType || (['image/png', 'image/webp'].includes(file.type) ? file.type : 'image/jpeg')
    };

    // Skip processing for SVG (vector format shouldn't be rasterized by canvas for simple resize)
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        return file;
    }

    let processingFile = file;

    // Handle HEIC/HEIF files (common on iPhone)
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (file.type === 'image/heic' || file.type === 'image/heif' || ext === 'heic' || ext === 'heif') {
        try {
            const heic2any = (await import('heic2any')).default;
            const convertedBlob = await heic2any({
                blob: file,
                toType: 'image/jpeg',
                quality: opts.quality
            });

            const blobArray = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            processingFile = new File([blobArray], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });
        } catch (err) {
            console.error('HEIC conversion failed, falling back to original file', err);
        }
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > height) {
                    if (width > opts.maxDimension) {
                        height = Math.round((height * opts.maxDimension) / width);
                        width = opts.maxDimension;
                    }
                } else {
                    if (height > opts.maxDimension) {
                        width = Math.round((width * opts.maxDimension) / height);
                        height = opts.maxDimension;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                // Use high-quality image smoothing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Canvas toBlob failed'));
                            return;
                        }

                        // Create a new file object from the blob
                        const resizedFile = new File([blob], processingFile.name, {
                            type: opts.mimeType,
                            lastModified: Date.now(),
                        });

                        resolve(resizedFile);
                    },
                    opts.mimeType,
                    opts.quality
                );
            };
            img.onerror = () => reject(new Error('Failed to load image for resizing'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file for resizing'));
        reader.readAsDataURL(processingFile);
    });
};

/**
 * Checks if a file is an image based on its extension or MIME type.
 * Useful for HEIC files which might not have a standard MIME type in some browsers.
 */
export const isImage = (file: File): boolean => {
    const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/svg+xml'];
    if (acceptedTypes.includes(file.type)) return true;

    // Fallback for extensions
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'svg'].includes(ext || '');
};
